<script lang="ts">
	import { OrderStatus, orderStatusNameMap } from "../../../../core/schema/OrderStatus.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import { DotSize } from "../../Dot.js";
	import { toWeekdayDateMonthYearFormat } from "../../../../core/utils/toWeekdayDateMonthYearFormat.js";
	import { PaymentStatus, paymentStatusNameMap } from "../../../../core/schema/PaymentStatus.js";
	import OrderDetail from "./OrderDetail.svelte";
	import Icon from "../Icon.svelte";
	import Link from "../Link.svelte";
	import Dot from "../Dot.svelte";
	import printer from "../../../../core/assets/icons/printer.svg?raw";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { getPriceOfOrderWithDiscounts } from "../../../../core/schema/utils/getPriceOfOrderWithDiscounts.js";
	import OrderCardContents from "./OrderCardContents.svelte";
	import { paymentStatusDotTypeMap } from "./paymentStatusDotTypeMap.js";
	import { paymentStatusTextColorMap } from "./paymentStatusTextColorMap.js";
	import { orderStatusDotTypeMap } from "./orderStatusDotTypeMap.js";
	import { orderStatusTextColorMap } from "./orderStatusTextColorMap.js";
	import { deliveryTypeNameMap } from "../../../../core/schema/names/deliveryTypeNameMap.js";
	import type { OrderForCustomer } from "../../../../core/schema/public/OrderForCustomer.js";

	export let products: ProductForCustomer[];
	export let order: OrderForCustomer;
	export let onRepeatOrder: (() => Promise<void>) | undefined = undefined;

	const globalSettings = getGlobalSettings();
	const { TENANT_LOCALE } = globalSettings;

	const { voucherValue, discountedPrice } = getPriceOfOrderWithDiscounts(order);
</script>

<div class="border-1 group rounded-2xl border-gray-200">
	<div class="grid w-full grid-cols-2 items-start gap-3 p-6 xl:flex xl:flex-row xl:gap-10">
		<OrderDetail label="Stav">
			<p class="whitespace-nowrap font-semibold">
				<Dot size={DotSize.Sm} type={orderStatusDotTypeMap[order.status]} />
				<span class={orderStatusTextColorMap[order.status]}>{orderStatusNameMap[order.status][TENANT_LOCALE]}</span>
			</p>
		</OrderDetail>
		<OrderDetail label="Číslo objednávky"><p class="font-semibold">#{order.orderNumber}</p></OrderDetail>
		<OrderDetail label="Termín dodání">
			<p class="font-semibold">{nbspify(toWeekdayDateMonthYearFormat(order.deliveryDate, TENANT_LOCALE))}</p>
		</OrderDetail>
		<OrderDetail label="Způsob doručení">
			<p class="font-semibold">
				{nbspify(deliveryTypeNameMap[order.deliveryType][TENANT_LOCALE])}
			</p>
		</OrderDetail>
		<OrderDetail label="Platba">
			<p class="whitespace-nowrap font-semibold">
				<Dot size={DotSize.Sm} type={paymentStatusDotTypeMap[order.payment.status]} />
				<span class={paymentStatusTextColorMap[order.payment.status]}>
					{nbspify(paymentStatusNameMap[order.payment.status][TENANT_LOCALE])}
				</span>
			</p>
		</OrderDetail>
		<span class="text-md self-end text-end font-semibold lg:hidden">
			{nbspify(formatPrice(discountedPrice, TENANT_LOCALE, false))}
		</span>
		<div class="col-span-2 mt-3 flex flex-col items-center justify-center gap-1 lg:hidden">
			{#if order.status !== OrderStatus.Cancelled && order.goPayData}
				<Link
					target="_blank"
					href={order.goPayData.url}
					asButton
					variant="primary"
					class="border-1 cursor-pointer rounded-full border-gray-800 px-3 py-1 hover:bg-gray-100"
				>
					<Icon icon={printer} class="h-5 w-5" />
					<span class="whitespace-nowrap">
						{#if order.payment.status === PaymentStatus.Paid}
							Zobrazit detail platby
						{:else}
							Přejít na platbu
						{/if}
					</span>
				</Link>
			{/if}
			{#if order.invoiceUrl}
				<Link
					target="_blank"
					href={order.invoiceUrl}
					asButton
					variant="transparent"
					class="border-1 cursor-pointer rounded-full border-gray-800 px-3 py-1 hover:bg-gray-100"
				>
					<Icon icon={printer} class="h-5 w-5" />
					<span class="whitespace-nowrap">Zobrazit fakturu</span>
				</Link>
			{:else if order.proformaUrl}
				<Link
					target="_blank"
					href={order.proformaUrl}
					asButton
					variant="transparent"
					class="border-1 cursor-pointer rounded-full border-gray-800 px-3 py-1 hover:bg-gray-100"
				>
					<Icon icon={printer} class="h-5 w-5" />
					<span class="whitespace-nowrap">Zobrazit proformu</span>
				</Link>
			{/if}
		</div>
		<div class="ml-auto hidden items-center gap-6 self-end lg:flex">
			<div class="flex flex-col items-start gap-1">
				{#if order.status !== OrderStatus.Cancelled && order.goPayData}
					<Link
						target="_blank"
						href={order.goPayData.url}
						asButton
						variant="primary"
						class="border-1 cursor-pointer rounded-full border-gray-800 px-3 py-1 hover:bg-gray-100"
					>
						<Icon icon={printer} class="h-5 w-5" />
						<span class="whitespace-nowrap">
							{#if order.payment.status === PaymentStatus.Paid}
								Zobrazit detail platby
							{:else}
								Přejít na platbu
							{/if}
						</span>
					</Link>
				{/if}
				{#if order.invoiceUrl}
					<Link
						target="_blank"
						href={order.invoiceUrl}
						asButton
						variant="transparent"
						class="border-1 cursor-pointer rounded-full border-gray-800 px-3 py-1 hover:bg-gray-100"
					>
						<Icon icon={printer} class="h-5 w-5" />
						<span class="whitespace-nowrap">Zobrazit fakturu</span>
					</Link>
				{:else if order.proformaUrl}
					<Link
						target="_blank"
						href={order.proformaUrl}
						asButton
						variant="transparent"
						class="border-1 cursor-pointer rounded-full border-gray-800 px-3 py-1 hover:bg-gray-100"
					>
						<Icon icon={printer} class="h-5 w-5" />
						<span class="whitespace-nowrap">Zobrazit proformu</span>
					</Link>
				{/if}
			</div>
			<span class="text-lg font-semibold">{nbspify(formatPrice(discountedPrice, TENANT_LOCALE, false))}</span>
		</div>
	</div>
	<OrderCardContents {order} {products} {onRepeatOrder} {voucherValue} />
</div>
